<template>
  <!-- The Recent cap confirmation. Its own component rather than a second dialog-modal block in each
       of the two components that can trigger the cap: the copy is identical in both, it is
       destructive, and two hand-maintained copies of a destructive confirm is exactly the kind of
       thing that drifts until one of them stops naming the conversation it is about to delete.

       Deliberately dumb — every piece of state is a prop and the only outputs are confirm/cancel.
       The owning component keeps the delete, the round-trip and the error, because what happens
       AFTER the confirmation differs: the list resumes an unpin, the chat window resumes a send. -->
  <dialog-modal :is-open="isOpen" :title="$t('rey.conversations.capTitle')"
                :show-close-button="true" :processing="processing"
                :close-on-backdrop-click="!processing"
                @close="$emit('cancel')">
    <!-- Same -mt-1 trim and the same two-line shape as the manual delete confirmation, so the two
         read as one dialog the user has seen before. DialogModal supplies the slot padding. -->
    <div class="flex flex-col gap-2 -mt-1">
      <p class="fusion-conversation-cap-victim text-text-default text-base font-semibold leading-snug">{{ victimTitle }}</p>
      <p class="text-text-subtle text-sm leading-snug">{{ body_text }}</p>
      <p v-if="error" class="text-danger text-sm leading-snug">{{ $t('rey.conversations.deleteError') }}</p>
    </div>
    <template #footer>
      <div class="w-full flex items-center justify-end gap-2 -mx-2 -mb-2 -mt-1">
        <button type="button"
                class="fusion-conversation-cap-cancel h-10 px-4 rounded-xl bg-btn-secondary text-text-default text-base font-semibold"
                :class="processing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
                :disabled="processing"
                @click.prevent="$emit('cancel')">
          {{ $t('common.cancel') }}
        </button>
        <!-- The danger BUTTON tokens, matching the manual delete confirmation: Tailwind maps
             `danger` to --text-error (a text colour), so bg-danger would paint error-text red rather
             than the button red. These two have dark-mode overrides in tokens.css. -->
        <button type="button"
                class="fusion-conversation-cap-confirm h-10 px-4 rounded-xl bg-[var(--button-danger-default)] text-[var(--text-button-danger)] text-base font-semibold flex items-center gap-2 hover:opacity-90"
                :class="processing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'"
                :disabled="processing"
                :aria-busy="processing ? 'true' : 'false'"
                @click.prevent="$emit('confirm')">
          <div v-if="processing" class="fusion-loader loader-sm inline-block"></div>
          {{ $t('rey.conversations.capConfirm') }}
        </button>
      </div>
    </template>
  </dialog-modal>
</template>

<script>
  import DialogModal from '@components/UI/DialogModal.vue';
  import { RECENT_CONVERSATION_CAP } from '../../utils/reyConversationCap';

  /**
   * "Recent is full — may I delete the oldest one?"
   *
   * Raised from two places, which is what `mode` distinguishes: unpinning a conversation would push
   * Recent over the cap, or creating one would. The conversation NAMED here is the one about to be
   * deleted (the last row in Recent), never the one the user is acting on.
   */
  export default {
    name: 'ReyConversationCapDialog',
    components: {
      'dialog-modal': DialogModal,
    },
    props: {
      isOpen: {
        type: Boolean,
        required: true,
        default: false,
      },
      // Which action is being held up. Only the body copy differs — the cap, the victim and the
      // consequence are the same either way.
      mode: {
        type: String,
        required: true,
        validator: value => ['create', 'unpin'].includes(value),
      },
      // The DISPLAYED title of the conversation to be deleted, resolved by the caller: the
      // placeholder labels ("New conversation", "Current Thread") are localized strings the list
      // substitutes for a name that is empty or an internal literal, and a dialog naming a
      // conversation the user cannot see by that name on screen is worse than naming none.
      victimTitle: {
        type: String,
        required: false,
        default: '',
      },
      processing: {
        type: Boolean,
        required: false,
        default: false,
      },
      error: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    emits: ['confirm', 'cancel'],
    computed: {
      // Spelled out as literal $t() calls rather than a key built from `mode`. The i18n spec finds
      // usages by scanning for $t('...'), and a key it cannot see reads as an orphan — the check
      // that catches genuinely dead strings. Same reason ReyConversationList.sectionEmptyLabel is
      // written this way.
      body_text() {
        if (this.mode === 'create')
          return this.$t('rey.conversations.capBodyCreate', { max: RECENT_CONVERSATION_CAP });
        return this.$t('rey.conversations.capBodyUnpin', { max: RECENT_CONVERSATION_CAP });
      },
    },
  }
</script>
